import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, 
  FileCheck, 
  MapPin, 
  Clock, 
  CreditCard, 
  Banknote,
  Star,
  ArrowRight,
  CheckCircle,
  Users,
  ShieldCheck,
  Quote,
  Heart,
  Sparkles,
  X,
  Gift,
  Package,
  Phone,
  Scale,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { useSiteContent } from '../contexts/SiteContent';
import axios from 'axios';

import { ToothIcon } from '../components/icons/ToothIcon';
import { ScalpelIcon } from '../components/icons/ScalpelIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_umzinto-doc-preview/artifacts/kc3lrf2a_pexels-thirdman-5327865.jpg";
const DENTAL_IMAGE = "https://images.unsplash.com/photo-1588776814546-daab30f310ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxkZW50aXN0JTIwdHJlYXRpbmclMjBwYXRpZW50JTIwc21pbGluZ3xlbnwwfHx8fDE3NzAyMTUzNDN8MA&ixlib=rb-4.1.0&q=85";

// Medical Aid Logos - Using uploaded images
const medicalAids = [
  { name: 'Discovery', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/29o82ppt_Screenshot%202023-12-21%20212903.png' },
  { name: 'GEMS', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/5l9zvrgi_gems-pic.png' },
  { name: 'Bonitas', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/tymm69x2_Screenshot%202023-12-21%20212351.png' },
  { name: 'Momentum', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/yo9gthuz_momentum-health_0%20%281%29.jpg' },
  { name: 'Polmed', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/ourz366t_Polmed-psbntcjfvwfcnyc6sar7ktxhsiwcewyl5d8tebd2bk.png' },
  { name: 'Medscheme', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/lqvm6pbz_Screenshot%202023-12-21%20213323.png' },
  { name: 'SAMWUMED', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/ia986jjv_Screenshot%202023-12-21%20213903.png' },
  { name: 'Medshield', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/d0s3agys_Screenshot%202023-12-21%20213922.png' },
  { name: 'Universal', logo: 'https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/twby58ua_Screenshot%202023-12-21%20214021.png' },
];

const services = [
  {
    icon: Stethoscope,
    title: 'Medical Services',
    description: 'Comprehensive GP consultations for adults and children, chronic care, HIV management, and more.',
    color: 'bg-teal-500',
    link: '/services#medical'
  },
  {
    icon: ToothIcon,
    title: 'Dental Services',
    description: 'Full dental care including cleanings, fillings, root canals, crowns, and dentures.',
    color: 'bg-blue-500',
    link: '/services#dental'
  },
  {
    icon: ScalpelIcon,
    title: 'Surgical Services',
    description: 'Minor surgical procedures including wound suturing, lipoma removal, and implanon removal.',
    color: 'bg-purple-500',
    link: '/services#surgical'
  },
  {
    icon: FileCheck,
    title: 'Insurance & Medicals',
    description: 'PrDP permits, fitness medicals, and insurance medicals for all your certification needs.',
    color: 'bg-amber-500',
    link: '/services#permits'
  },
];

const whyChooseUs = [
  { icon: Banknote, text: 'Affordable, personalised rates' },
  { icon: Users, text: 'Walk-in consultations welcome' },
  { icon: ShieldCheck, text: 'All major medical aids and insurance accepted' },
  { icon: CreditCard, text: 'Card & cash payments accepted' },
];

// Animated Medical Aids Marquee Component
const MedicalAidsMarquee = () => {
  // Filter to only show medical aids with logos
  const aidsWithLogos = medicalAids.filter(aid => aid.logo);
  
  return (
    <div className="relative overflow-hidden py-6">
      {/* Gradient overlays for smooth fade effect */}
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10" />
      
      {/* Scrolling container */}
      <div className="flex animate-marquee items-center">
        {/* First set */}
        {aidsWithLogos.map((aid, index) => (
          <div
            key={`first-${index}`}
            className="flex-shrink-0 mx-4 sm:mx-6"
          >
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-3 sm:p-4 w-32 h-16 sm:w-40 sm:h-20 flex items-center justify-center">
              <img
                src={aid.logo}
                alt={aid.name}
                className="max-h-12 sm:max-h-14 w-auto object-contain"
              />
            </div>
          </div>
        ))}
        {/* Duplicate set for seamless loop */}
        {aidsWithLogos.map((aid, index) => (
          <div
            key={`second-${index}`}
            className="flex-shrink-0 mx-4 sm:mx-6"
          >
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-3 sm:p-4 w-32 h-16 sm:w-40 sm:h-20 flex items-center justify-center">
              <img
                src={aid.logo}
                alt={aid.name}
                className="max-h-12 sm:max-h-14 w-auto object-contain"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Google Reviews Component
const GoogleReviews = () => {
  const [allReviews, setAllReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rating, setRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    try {
      const dailyRes = await axios.get(`${API}/reviews/daily`);
      const approvedReviews = dailyRes.data || [];
      
      const googleRes = await axios.get(`${API}/reviews/google`);
      setRating(googleRes.data.rating);
      setTotalReviews(googleRes.data.total_reviews);
      
      if (approvedReviews.length > 0) {
        setAllReviews(approvedReviews);
      } else {
        setAllReviews(googleRes.data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setAllReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (allReviews.length <= 3) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % allReviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [allReviews]);

  // Get 3 reviews starting from currentIndex, wrapping around
  const getVisibleReviews = () => {
    if (allReviews.length === 0) return [];
    if (allReviews.length <= 3) return allReviews;
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(allReviews[(currentIndex + i) % allReviews.length]);
    }
    return visible;
  };

  const reviews = getVisibleReviews();

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Reviews Summary Card */}
      <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden">
        <CardContent className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-teal-100 rounded-full p-4">
              <Star className="w-8 h-8 text-teal-600 fill-teal-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Google Reviews</h3>
              {rating && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-slate-900">{rating}</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} 
                      />
                    ))}
                  </div>
                  {totalReviews && (
                    <span className="text-slate-500 text-sm">({totalReviews} reviews)</span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <p className="text-slate-600 mb-6">
            See what our patients are saying about their experience at K&K Medical and Dental.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              asChild
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="view-reviews-btn"
            >
              <a 
                href="https://www.google.com/maps/place/?q=place_id:ChIJMUVpO3Q59h4R2uVVVUP1kmA" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View on Google
              </a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-6 transition-all duration-300"
              data-testid="write-review-btn"
            >
              <a 
                href="https://g.page/r/CdrlVVVD9ZJgEBM/review" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Write a Review
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reviews Display */}
      <div className="space-y-4 notranslate">
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Card key={i} className="border-0 shadow-md rounded-xl animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
                  <div className="h-3 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : reviews.length > 0 ? (
          reviews.slice(0, 3).map((review, index) => (
            <Card 
              key={review.review_id || `${review.author_name}-${review.time || index}`} 
              className="border-0 shadow-md rounded-xl hover:shadow-lg transition-shadow duration-300 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {review.profile_photo_url ? (
                      <img 
                        src={review.profile_photo_url} 
                        alt={review.author_name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-teal-600 font-semibold">
                          {review.author_name?.charAt(0) || 'A'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-slate-900">{review.author_name}</h4>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mb-2">{review.relative_time_description}</p>
                    <p className="text-slate-600 text-sm line-clamp-3">{review.text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="border-0 shadow-md rounded-xl">
            <CardContent className="p-6 text-center">
              <Quote className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">
                Visit our Google Business page to see what our patients are saying!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Promo Popup Component
const PromoPopup = ({ onClose }) => {
  const showPricingNote = new Date() < new Date('2026-05-01T00:00:00');

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" data-testid="promo-popup-overlay">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Popup container — flex column, scrollable on mobile */}
      <div className="relative min-h-full flex items-start sm:items-center justify-center p-3 sm:p-4">
        <div
          className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] my-2 sm:my-0 animate-fade-in-up"
          data-testid="promo-popup"
        >
          {/* Close button — fixed on the popup, always reachable */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 bg-white hover:bg-slate-50 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 ring-1 ring-slate-200"
            aria-label="Close popup"
            data-testid="promo-popup-close"
          >
            <X className="w-5 h-5 text-slate-700" />
          </button>

          {/* Header — sticky/non-scrolling */}
          <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-5 sm:p-6 text-center rounded-t-2xl sm:rounded-t-3xl flex-shrink-0">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-2xl mb-2 sm:mb-3">
              <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Don't Miss Out!</h2>
            <p className="text-white/90 text-xs sm:text-sm">Exclusive offers for you and your family</p>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 overscroll-contain">
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {showPricingNote && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 sm:p-3 text-center">
                  <p className="text-amber-800 text-xs sm:text-sm font-medium">All new pricing effective from 1st May 2026</p>
                </div>
              )}

              <Link
                to="/weight-loss"
                onClick={onClose}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-100 hover:shadow-md transition-all duration-200 group"
                data-testid="promo-popup-weight-loss-link"
              >
                <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl p-2.5 sm:p-3 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                  <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">Weight Loss & Wellness</h3>
                  <p className="text-slate-600 text-xs sm:text-sm">Doctor-led programme</p>
                </div>
                <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>

              <Link
                to="/prizes"
                onClick={onClose}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 hover:shadow-md transition-all duration-200 group"
                data-testid="promo-popup-prizes-link"
              >
                <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-2.5 sm:p-3 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                  <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">Up to R2,000 in Prizes Monthly</h3>
                  <p className="text-slate-600 text-xs sm:text-sm">View this month's prize draw</p>
                </div>
                <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">WIN</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>

              <Link
                to="/promotions"
                onClick={onClose}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 hover:shadow-md transition-all duration-200 group"
                data-testid="promo-popup-promotions-link"
              >
                <div className="bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl p-2.5 sm:p-3 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                  <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">View Promotions</h3>
                  <p className="text-slate-600 text-xs sm:text-sm">Combos, loyalty rewards & monthly draws</p>
                </div>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>

              <Link
                to="/packages"
                onClick={onClose}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 hover:shadow-md transition-all duration-200 group"
                data-testid="promo-popup-packages-link"
              >
                <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl p-2.5 sm:p-3 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                  <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">Healthcare Packages</h3>
                  <p className="text-slate-600 text-xs sm:text-sm">Antenatal, Diabetic & HIV monitoring</p>
                </div>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            </div>
          </div>

          {/* Footer — non-scrolling */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-1 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-2"
              data-testid="promo-popup-dismiss"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Home = () => {
  const [showPromo, setShowPromo] = useState(false);
  const { content } = useSiteContent();

  useEffect(() => {
    const dismissed = sessionStorage.getItem('promo_popup_dismissed');
    if (!dismissed) {
      const timer = setTimeout(() => setShowPromo(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClosePromo = () => {
    setShowPromo(false);
    sessionStorage.setItem('promo_popup_dismissed', 'true');
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {showPromo && <PromoPopup onClose={handleClosePromo} />}
      <SEO 
        title="Home"
        description="K&K Medical and Dental - Quality healthcare in Umzinto & Scottburgh, KZN. Walk-ins welcome. All major medical aids accepted including Discovery, GEMS, Bonitas, Momentum."
        keywords="doctor Umzinto, doctor Scottburgh, dentist Scottburgh, GP KZN, medical clinic, dental clinic, affordable healthcare, walk-in clinic, Discovery doctor, GEMS doctor"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              {/* Quote CTA Banner - Prominent */}
              <Link to="/prices" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-full shadow-lg mb-6 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group">
                <Banknote className="w-5 h-5 group-hover:animate-bounce" />
                <span className="font-bold text-lg">{content.promo?.hero_text || 'Quality healthcare. Personalised rates.'}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
                Your One-Stop Shop for 
                <span className="text-teal-600"> Medical & Dental Care</span>
              </h1>
              <p className="text-lg font-semibold text-teal-700 mb-3 italic">"Low rates do not mean lower standards."</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                From GP consultations to dental procedures, surgical services, and chronic care — everything you need under one roof. Walk-in consultations available, no appointment needed!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  data-testid="hero-promotions-btn"
                >
                  <Link to="/" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="location-section"]')?.scrollIntoView({ behavior: 'smooth' }); }} className="inline-flex items-center gap-2">
                    <MapPin className="w-5 h-5 group-hover:animate-pulse" />
                    Visit Us
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                  data-testid="hero-virtual-btn"
                >
                  <a href="/booking" onClick={() => window.scrollTo(0, 0)}>Virtual Consult</a>
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-4 text-slate-600">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium">Mon-Fri: 8AM-5PM | Sat: 8AM-2PM</span>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Doctor consultation" 
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent p-6">
                  <div className="flex items-center justify-between">
                    <Link 
                      to="/prices" 
                      onClick={() => window.scrollTo(0, 0)}
                      className="flex items-center gap-4 group cursor-pointer"
                      data-testid="hero-price-link"
                    >
                      <div className="bg-emerald-500 rounded-full p-3 animate-pulse group-hover:scale-110 transition-transform">
                        <Banknote className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-white">
                        <p className="text-sm opacity-90">Affordable Healthcare</p>
                        <p className="text-3xl font-bold text-emerald-400 group-hover:text-emerald-300 transition-colors">{content.promo?.banner_text || 'Get a Quote'}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Link>
                    {/* Walk-ins badge - moved inside the gradient bar */}
                    <div className="hidden sm:flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-white font-medium text-sm">Walk-ins Welcome</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Price Highlight Banner - Full Width */}
        <div className="bg-teal-600 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-white text-center">
              <div className="flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                <span className="font-semibold">{content.promo?.home_note || 'WhatsApp us for current rates'}</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-white/30" />
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span className="font-semibold">All Medical Aids Accepted</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-white/30" />
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="font-semibold">Walk-ins Welcome</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Aids Section - Animated Logos */}
      <section className="py-12 bg-white border-y border-slate-100" data-testid="medical-aids-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-2">Accepted Medical Aids</p>
            <h2 className="text-xl font-semibold text-slate-900">We accept all major medical aids</h2>
          </div>
          <MedicalAidsMarquee />
          <div className="flex justify-center items-center gap-6 mt-4">
            <div className="flex items-center gap-2 text-slate-600">
              <CreditCard className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-medium">Visa & Mastercard</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Banknote className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-medium">Cash Accepted</span>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Patient Reviews</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              What Our Patients Say
            </h2>
          </div>
          <GoogleReviews />
        </div>
      </section>

      {/* Inclusive Healthcare Message */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="inclusive-healthcare-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-teal-500/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-teal-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Healthcare That Puts People First
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">
            We believe that quality healthcare is a right, not a privilege. Our practice is committed to 
            making essential medical and dental services accessible to everyone in our community. 
            With streamlined pricing and a patient-centred approach, we've removed unnecessary barriers 
            so you can focus on what matters most — your health and wellbeing.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white">
              <span className="text-teal-400 font-semibold">✓</span> Transparent Pricing
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white">
              <span className="text-teal-400 font-semibold">✓</span> No Hidden Costs
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white">
              <span className="text-teal-400 font-semibold">✓</span> Community Focused
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Our Services</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From routine check-ups to specialized treatments, we provide quality care for you and your family.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.title} 
                className={`bg-white border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 rounded-2xl overflow-hidden animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className={`${service.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">{service.description}</p>
                  <Link 
                    to={service.link} 
                    className="inline-flex items-center gap-2 text-teal-600 font-medium text-sm hover:gap-3 transition-all duration-200"
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Why Choose Us</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Affordable Quality Care for the Whole Family
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                At <span className="notranslate">K&K Medical and Dental</span>, we believe everyone deserves access to quality healthcare. 
                We offer convenient walk-in availability, support for all major medical aids, and personalised pricing tailored to each patient's needs.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {whyChooseUs.map((item) => (
                  <div 
                    key={item.text} 
                    className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 hover:bg-teal-50 transition-colors duration-200"
                  >
                    <div className="bg-teal-100 rounded-lg p-2">
                      <item.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <span className="text-slate-700 font-medium text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src={DENTAL_IMAGE} 
                  alt="Dental treatment" 
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Your Visit */}
      <section className="py-16 lg:py-24 bg-white" data-testid="schedule-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 lg:p-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-200 mb-3">Ready to Visit?</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Schedule Your Visit</h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Book an in-person appointment or schedule a virtual consultation — whichever suits you best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-lg"
                data-testid="schedule-book-btn"
              >
                <a href="/booking" onClick={() => window.scrollTo(0, 0)}>Book Appointment</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-700 rounded-full px-8 py-6 text-lg transition-all duration-300"
                data-testid="schedule-virtual-btn"
              >
                <a href="/booking" onClick={() => window.scrollTo(0, 0)}>Virtual Consult</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Our Locations</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Two Convenient Locations
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Umzinto Branch */}
            <div className="flex flex-col">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100 h-[250px] mb-4" data-testid="map-umzinto">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3444.4538888895986!2d30.660777275248833!3d-30.30960487479112!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef639743b694531%3A0x6092f5435555e5da!2sK%20and%20K%20Medical%20and%20Dental%20(Umzinto)!5e0!3m2!1sen!2sza!4v1777408413521!5m2!1sen!2sza"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="K&K Medical - Umzinto Location"
                />
              </div>
              <div className="bg-slate-900 rounded-3xl p-8 lg:p-10 flex-1 flex flex-col" data-testid="location-umzinto">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block px-3 py-1 rounded-full bg-teal-500/20 text-teal-400 text-xs font-semibold">Main Branch</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-white mb-6 notranslate">
                K&K Medical & Dental — Umzinto
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-teal-500/20 rounded-full p-3">
                    <MapPin className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Address</p>
                    <p className="text-slate-400 text-sm notranslate">
                      Lot 293 Nelson Mandela Road,<br />
                      Umzinto, KZN, South Africa
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-500/20 rounded-full p-3">
                    <Phone className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Telephone</p>
                    <a href="tel:0399741570" className="text-teal-400 hover:text-teal-300 text-sm font-medium transition-colors">039 974 1570</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-500/20 rounded-full p-3">
                    <Clock className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Operating Hours</p>
                    <p className="text-slate-400 text-sm">
                      Mon - Fri: 8:00 AM - 5:00 PM<br />
                      Saturday: 8:00 AM - 2:00 PM<br />
                      Sun & Holidays: Closed
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-500/20 rounded-full p-3">
                    <Stethoscope className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">GP Availability</p>
                    <p className="text-slate-400 text-sm">
                      Mon - Fri: 10:00 AM - 5:00 PM<br />
                      Saturday: 8:00 AM - 2:00 PM (Full day)
                    </p>
                    <p className="text-amber-400/80 text-xs mt-1 italic">*Applies to General Practitioner only. Subject to change without prior notice.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-500/20 rounded-full p-3">
                    <CheckCircle className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Services</p>
                    <p className="text-slate-400 text-sm">
                      All Medical Services, Surgical, Insurance & Medicals, IV Infusions (Limited)
                    </p>
                    <p className="text-slate-500 text-xs mt-1">Dental and Specialist services not available at this branch</p>
                  </div>
                </div>
              </div>
              <Button 
                asChild
                className="mt-auto bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="get-directions-umzinto-btn"
              >
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=-30.3096049,30.6633522&destination_place_id=ChIJMUVpO3Q59h4R2uVVVUP1kmA" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Get Directions
                </a>
              </Button>
              </div>
            </div>

            {/* Scottburgh Branch */}
            <div className="flex flex-col">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100 h-[250px] mb-4" data-testid="map-scottburgh">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13781.116818475817!2d30.732788355419913!3d-30.286112199999987!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef637d043db8fc5%3A0x1fd80926d2642b49!2sK%20and%20K%20Medical%20and%20Dental%20-%20Scottburgh!5e0!3m2!1sen!2sza!4v1777408488726!5m2!1sen!2sza"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="K&K Medical - Scottburgh Location"
                />
              </div>
              <div className="bg-slate-900 rounded-3xl p-8 lg:p-10 flex-1 flex flex-col" data-testid="location-scottburgh">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-semibold">New Branch</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-white mb-6 notranslate">
                K&K Medical & Dental — Scottburgh
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 rounded-full p-3">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Address</p>
                    <p className="text-slate-400 text-sm notranslate">
                      Scott Health Medical Center,<br />
                      41 Airth Street,<br />
                      Scottburgh, KZN
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 rounded-full p-3">
                    <Phone className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Telephone</p>
                    <a href="tel:0393155041" className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">039 315 5041</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 rounded-full p-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Operating Hours</p>
                    <p className="text-slate-400 text-sm">
                      Mon - Fri: 8:00 AM - 5:00 PM<br />
                      Saturday: 8:00 AM - 2:00 PM<br />
                      Sun & Holidays: Closed
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 rounded-full p-3">
                    <Stethoscope className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">GP Availability</p>
                    <p className="text-slate-400 text-sm">
                      Mon - Fri: 8:00 AM - 10:00 AM
                    </p>
                    <p className="text-amber-400/80 text-xs mt-1 italic">*Applies to General Practitioner only. Subject to change without prior notice.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 rounded-full p-3">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Services</p>
                    <p className="text-slate-400 text-sm">
                      All Services including Dental & Specialist Services
                    </p>
                  </div>
                </div>
              </div>
              <Button 
                asChild
                className="mt-auto bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="get-directions-scottburgh-btn"
              >
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=-30.2861122,30.7508128" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Get Directions
                </a>
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};
